<template>
  <div class="relative inline-block w-max" ref="triggerRef" :class="customClass">
    <!-- Trigger Button -->
    <button
      ref="triggerButtonRef"
      type="button"
      @click.stop="toggleDropdown"
      class="dropdown-trigger"
      :class="triggerStyle ?? 'px-4 py-2 bg-blue-500 text-white rounded w-full'"
      :aria-haspopup="menu ? true : undefined"
      :aria-expanded="menu ? isOpen : undefined"
      :aria-label="triggerAriaLabel"
    >
      <slot name="trigger">Actions</slot>
    </button>

    <!-- Dropdown Content -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isOpen"
          ref="dropdownRef"
          class="dropdown-menu bg-surface shadow-md rounded-lg w-max"
          :role="menu ? 'menu' : undefined"
          :style="{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: dropdownPosition.minWidth,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: dropdownPosition.maxHeight,
            overflowY: dropdownPosition.maxHeight ? 'auto' : undefined,
          }"
          @click.stop="handleDropdownClick"
        >
          <slot name="contents"></slot>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { activeDropdownId, openDropdownHierarchy } from '@/composables/globalDropdown';
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { placeDropdown } from './dropdownPlacement';

const props = defineProps<{
  triggerStyle?: string;
  closeOnOutsideClick?: boolean;
  closeOnSelect?: boolean;
  customClass?: string;
  /**
   * Opt in to menu semantics: role="menu", aria-haspopup/expanded on the trigger,
   * arrow-key movement between items, Escape to close, and focus handed back to
   * the trigger on close (AB#21204).
   *
   * Opt-in rather than default on purpose. This component already has a dozen
   * callers using it as a panel of arbitrary content rather than a list of
   * actions, and announcing those as menus to a screen reader would be a lie.
   */
  menu?: boolean;
  /** What the trigger is called when its visible label is an icon or an ellipsis. */
  triggerAriaLabel?: string;
}>();
const emits = defineEmits(['open']);
const NAV_HEIGHT = 70; // adjust as per your navbar height
const id = Math.random().toString(36).substring(2, 9);
const parentId = inject<string | null>('dropdownParentId', null);
provide('dropdownParentId', id);

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const triggerButtonRef = ref<HTMLButtonElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);

interface DropdownPositionType {
  top: string;
  left: string;
  minWidth: string;
  maxHeight?: string;
}

const dropdownPosition = ref<DropdownPositionType>({
  top: '0px',
  left: '0px',
  minWidth: 'auto',
});

const toggleDropdown = async () => {
  if (!isOpen.value) {
    const newHierarchy = parentId
      ? [...openDropdownHierarchy.value.filter((i) => i !== id), id]
      : [id];
    openDropdownHierarchy.value = newHierarchy;
    activeDropdownId.value = id;
  } else {
    openDropdownHierarchy.value = [];
    activeDropdownId.value = null;
  }
};

const positionDropdown = async () => {
  await nextTick();

  requestAnimationFrame(() => {
    if (!triggerRef.value || !dropdownRef.value) return;

    const triggerRect = triggerRef.value.getBoundingClientRect();
    const panel = dropdownRef.value;
    // The panel's natural height, even while a previous placement has capped it.
    const height = panel.scrollHeight + (panel.offsetHeight - panel.clientHeight);

    const { top, left, maxHeight } = placeDropdown(
      triggerRect,
      { width: panel.getBoundingClientRect().width, height },
      { width: window.innerWidth, height: window.innerHeight }
    );

    dropdownPosition.value = {
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${triggerRect.width}px`,
      maxHeight: maxHeight === null ? undefined : `${maxHeight}px`,
    };
  });
};

const handleDropdownClick = () => {
  if (props.closeOnSelect !== false) {
    const index = openDropdownHierarchy.value.indexOf(id);
    if (index !== -1) {
      openDropdownHierarchy.value = openDropdownHierarchy.value.slice(0, index);
      activeDropdownId.value =
        openDropdownHierarchy.value[openDropdownHierarchy.value.length - 1] ?? null;
    }
  }
};

const handleOutsideClick = (event: MouseEvent) => {
  if (props.closeOnOutsideClick === false) return;

  const target = event.target as HTMLElement;
  const clickedInside = triggerRef.value?.contains(target) || dropdownRef.value?.contains(target);

  if (!clickedInside) {
    openDropdownHierarchy.value = [];
    activeDropdownId.value = null;
  }
};

const isElementInViewport = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();

  return (
    rect.top >= NAV_HEIGHT &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
};

const handleScroll = () => {
  if (!triggerRef.value) return;

  if (!isElementInViewport(triggerRef.value)) {
    if (isOpen.value) {
      openDropdownHierarchy.value = [];
      activeDropdownId.value = null;
    }
  } else if (isOpen.value) {
    positionDropdown();
  }
};

/* ---------------------------------------------------------------------------
 * Keyboard, for callers that opted into `menu` (AB#21204).
 *
 * The menu is teleported to <body>, so a listener on the wrapper would never
 * see these keys — it goes on the document, and only while this dropdown is the
 * open one.
 * ------------------------------------------------------------------------- */

const items = (): HTMLElement[] =>
  dropdownRef.value
    ? Array.from(dropdownRef.value.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'))
    : [];

const close = (returnFocus = false) => {
  openDropdownHierarchy.value = [];
  activeDropdownId.value = null;
  // Returning focus is the part people skip, and it is the part that matters:
  // without it a keyboard user is dropped at the top of the document.
  if (returnFocus) nextTick(() => triggerButtonRef.value?.focus());
};

const moveFocus = (step: number) => {
  const list = items();
  if (!list.length) return;

  const current = list.indexOf(document.activeElement as HTMLElement);
  const next = current === -1 ? (step > 0 ? 0 : list.length - 1) : (current + step + list.length) % list.length;
  list[next]?.focus();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.menu || !isOpen.value) return;

  switch (event.key) {
    case 'Escape':
      event.preventDefault();
      close(true);
      break;
    case 'ArrowDown':
      event.preventDefault();
      moveFocus(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      moveFocus(-1);
      break;
    case 'Home':
      event.preventDefault();
      items()[0]?.focus();
      break;
    case 'End':
      event.preventDefault();
      items().slice(-1)[0]?.focus();
      break;
    case 'Tab':
      // Tabbing out of a menu closes it, the same as clicking away would.
      close();
      break;
  }
};

watch(openDropdownHierarchy, async (chain) => {
  const willBeOpen = chain.includes(id);
  isOpen.value = willBeOpen;
  emits('open', willBeOpen);
});

watch(isOpen, async (visible) => {
  if (visible) {
    await positionDropdown();
    if (props.menu) {
      await nextTick();
      items()[0]?.focus();
    }
  }
});

// The Filters panel grows after it opens (counts arrive, a filter is added), so
// re-place it when it changes size rather than only on open.
useResizeObserver(dropdownRef, () => {
  if (isOpen.value) positionDropdown();
});

onMounted(() => {
  useEventListener(document, 'click', handleOutsideClick);
  useEventListener(document, 'keydown', handleKeydown);

  window.addEventListener('scroll', handleScroll, true);
  useEventListener(window, 'resize', handleScroll, true);
});

/** AB#21490 — a panel with its own "done" button (the Filters panel) closes itself through this. */
defineExpose({ close });

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleScroll);
});
</script>

<style scoped>
.dropdown-trigger {
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-menu {
  /* AB#21862 — teleported to <body>; clears drawers (10036) and modals (10030).
     See the layer scale in Shared/Drawer.vue. */
  z-index: 10060;
  border: 1px solid var(--border);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

/* Vue transition classes */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
